import { FormikValues, getIn } from "formik";

import { useFieldError } from "../forms/missing-fields/hooks/useFieldError";
import { YesNoAutocomplete } from "../yes-no-autocomplete/YesNoAutocomplete";
import { FormikFieldProps, OptionalOrRequired } from "./FormikField.types";
import { getFieldPath } from "./FormikField.utils";
import { useOptionalLabel } from "./useOptionalLabel";

// Shared Formik Yes/No field: an MUI Autocomplete limited to Yes/No (typeable → filters the two options;
// the value stays a boolean, `null` = unset) bound to Formik via the shared field convention (typed
// `fieldName`/`path`, `optional`/`required`). Owns the binding (getIn / setFieldValue / error
// extraction), the optional-label suffix, and the Ja/Nein labels (defaulted from the shared namespace,
// override via props). Long, sentence-length questions can render above the field via `labelAbove`.
export type FormikYesNoAutocompleteProps<T extends FormikValues> = FormikFieldProps<T> &
  OptionalOrRequired & {
    yesLabel?: string;
    noLabel?: string;
    onValueChange?: (value: boolean | null) => void;
    labelAbove?: boolean;
    id?: string;
    ariaLabelledBy?: string;
    disableClearable?: boolean;
  };

export function FormikYesNoAutocomplete<T extends FormikValues>({
  formik,
  path,
  fieldName,
  yesLabel,
  noLabel,
  label,
  helperText,
  required,
  optional,
  onValueChange,
  labelAbove,
  id,
  ariaLabelledBy,
  disableClearable,
}: FormikYesNoAutocompleteProps<T>) {
  const name = getFieldPath(path, fieldName);
  const resolvedLabel = useOptionalLabel(label, optional);

  const value = (getIn(formik.values, name) ?? null) as boolean | null;
  const error = useFieldError(formik, name, label);
  const helperMessage = error ?? helperText;

  return (
    <YesNoAutocomplete
      id={id}
      value={value}
      onChange={(next) => {
        onValueChange?.(next);
        void formik.setFieldValue(name, next);
      }}
      label={resolvedLabel}
      labelAbove={labelAbove}
      required={required}
      error={!!error}
      helperText={helperMessage}
      yesLabel={yesLabel}
      noLabel={noLabel}
      ariaLabelledBy={ariaLabelledBy}
      disableClearable={disableClearable}
    />
  );
}
